import React, { useState, useEffect, useRef, useCallback } from 'react';
import { delayTracker } from '../services/api';
import { getCurrentUser } from '../services/auth';
import { toTitleCase } from '../utils/format';

/**
 * Communications > Delay Tracker
 *
 * Rachael's replacement for the hand-kept Delays.xlsx. One row per late package,
 * pre-filled from the order record and from UPS, then assigned and worked.
 *
 * THIS PAGE DERIVES NOTHING. Every figure, flag and date comes from
 * lib/delay-tracker.js, so the grid, the email and any later report cannot start
 * telling three different stories about the same package.
 *
 * SAVES ARE DEBOUNCED AND SILENT, AND THEY NEVER RELOAD THE GRID. The Approvals
 * screen shipped with a save that called load(), which flipped the whole page to
 * "Loading..." on every tab out of a field; it read as the form refreshing itself
 * and took a while to place. Here the row is updated in local state, the write
 * goes out quietly behind it, and only an error is ever surfaced.
 */

const DASH = String.fromCharCode(8212);
// Local calendar dates, not UTC: after 8 PM Eastern, toISOString() already says tomorrow.
const localIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayIso = () => localIso(new Date());
const daysAgoIso = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return localIso(d); };
// The page opens on the current week (Sunday through today), per Rachael.
const weekStartIso = () => daysAgoIso(new Date().getDay());

// Spreadsheet grid: fixed widths, real cell borders, alternating shading. Her
// visual note, and it is the right call - people are reading across 10 columns.
const TH = {
  padding: '7px 8px', fontSize: 10, fontWeight: 700, textAlign: 'left',
  color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.4px',
  background: 'var(--gray-50)', borderBottom: '2px solid var(--gray-200)',
  borderRight: '1px solid var(--gray-200)', whiteSpace: 'nowrap',
};
const TD = {
  padding: '4px 8px', fontSize: 11.5, verticalAlign: 'top',
  borderBottom: '1px solid var(--gray-100)', borderRight: '1px solid var(--gray-100)',
};
const INPUT = {
  width: '100%', border: '1px solid transparent', background: 'transparent',
  fontSize: 11.5, fontFamily: 'inherit', color: 'var(--navy)', padding: '3px 4px',
  borderRadius: 3, resize: 'vertical',
};
const SELECT = { ...INPUT, border: '1px solid var(--gray-200)', background: '#fff', cursor: 'pointer' };

// Tracking and order stay put when the grid scrolls sideways, which is her note
// about small screens - those two columns are how you know which row you are on.
const stickyCell = (left, z, bg) => ({
  position: 'sticky', left, zIndex: z, background: bg,
  boxShadow: left > 0 ? '2px 0 0 rgba(0,0,0,0.04)' : undefined,
});

const COL_TRACK_W = 158;
const COL_ORDER_W = 96;

export default function DelayTracker({ navigateTo }) {
  const user = getCurrentUser();
  const who = user?.staffName || user?.id || 'CPx';

  const [start, setStart] = useState(weekStartIso());
  const [end, setEnd] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(0);
  const [saveError, setSaveError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);
  const [expanded, setExpanded] = useState({});

  const timers = useRef({});
  const enrichPoll = useRef(null);

  const load = useCallback(async (opts = {}) => {
    // A quick-pick passes its range in directly; state has not updated yet.
    const startDate = opts.start || start;
    const endDate = opts.end || end;
    if (!opts.quiet) setLoading(true);
    setError(null);
    try {
      const res = await delayTracker('report', { startDate, endDate });
      setData(res);
      // Anything UPS has not been asked about yet gets queued, then we poll until
      // the outstanding count reaches zero. It converges: a delivered package's
      // reason is final and is never looked up twice.
      if (res.outstandingUpsLookups > 0) {
        await delayTracker('enrich', { startDate, endDate });
        if (!enrichPoll.current) {
          enrichPoll.current = setInterval(async () => {
            try {
              const r = await delayTracker('report', { startDate, endDate });
              setData(r);
              if (!r.outstandingUpsLookups) {
                clearInterval(enrichPoll.current);
                enrichPoll.current = null;
              }
            } catch (_) { /* keep polling; a blip should not kill the page */ }
          }, 6000);
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => {
    if (enrichPoll.current) clearInterval(enrichPoll.current);
    Object.values(timers.current).forEach(clearTimeout);
  }, []);

  /* --- editing ----------------------------------------------------------
     Local state moves immediately; the write is debounced 700ms per row and
     never triggers a reload. `assignedTo` and `resolved` are the two that
     matter downstream, so they are written straight away rather than waiting
     out a debounce that a page-close would cancel.                         */
  const patchRow = (tracking, patch, immediate) => {
    setData((d) => d && ({
      ...d,
      rows: d.rows.map((r) => (r.trackingNumber === tracking ? { ...r, ...patch } : r)),
    }));

    const row = data?.rows.find((r) => r.trackingNumber === tracking);
    const send = async () => {
      setSaving((n) => n + 1);
      try {
        await delayTracker('save', {
          trackingNumber: tracking,
          orderId: row?.orderId,
          bcOrderId: row?.bcOrderId,
          who,
          ...patch,
        });
        setSaveError(null);
      } catch (e) {
        setSaveError(e.message);
      } finally {
        setSaving((n) => n - 1);
      }
    };

    if (timers.current[tracking]) clearTimeout(timers.current[tracking]);
    if (immediate) send();
    else timers.current[tracking] = setTimeout(send, 700);
  };

  const openSettings = async () => {
    setShowSettings(true);
    try { setSettings(await delayTracker('config', {})); } catch (e) { setSaveError(e.message); }
  };

  const rows = data?.rows || [];
  const open = rows.filter((r) => !r.resolved);
  const done = rows.filter((r) => r.resolved);
  const c = data?.counts;

  /* --- one row ---------------------------------------------------------- */
  const renderRow = (r, i, isResolved) => {
    const bg = isResolved ? '#fafafa' : (i % 2 === 0 ? '#fff' : 'var(--gray-50)');
    const exceptions = r.upsExceptions || [];
    const isOpen = !!expanded[r.trackingNumber];

    // "Suspected or unconfirmed": UPS's own scheduled date disagrees with the
    // date we promised. That is a real signal rather than a guess, and it is the
    // only thing here that earns the red.
    const promisedUnconfirmed = !!(r.upsScheduledDate && r.promisedDate && r.upsScheduledDate !== r.promisedDate);

    return (
      <tr key={r.trackingNumber} style={{ background: bg, color: isResolved ? 'var(--gray-500)' : undefined }}>
        <td style={{ ...TD, ...stickyCell(0, 2, bg), width: COL_TRACK_W, fontFamily: 'monospace', fontSize: 10.5 }}>
          <a
            href={`https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(r.trackingNumber)}`}
            target="_blank" rel="noreferrer"
            style={{ color: 'var(--navy)', textDecoration: 'none' }}
          >{r.trackingNumber}</a>
        </td>

        <td style={{ ...TD, ...stickyCell(COL_TRACK_W, 2, bg), width: COL_ORDER_W }}>
          <span
            onClick={() => navigateTo?.('order-details', r.bcOrderId || r.orderId)}
            style={{ color: 'var(--orange)', fontWeight: 600, cursor: 'pointer' }}
          >{r.orderNumber || DASH}</span>
          {r.orderNumberIsInternal && (
            <span title="Phone or manually-created order: no BigCommerce number, so this is the internal id."
              style={{ fontSize: 9, marginLeft: 4, color: 'var(--gray-400)', fontWeight: 700 }}>int</span>
          )}
          {r.customer && <div style={{ fontSize: 9.5, color: 'var(--gray-500)' }}>{toTitleCase(r.customer)}</div>}
        </td>

        <td style={{ ...TD, width: 62 }}>{r.shipMethod || DASH}</td>

        {/* Expected. N/A in red when we have none; red when UPS disagrees with it. */}
        <td style={{ ...TD, width: 92, textAlign: 'center' }}>
          {r.promisedDate ? (
            <span style={{ color: promisedUnconfirmed ? 'var(--red, #c0392b)' : undefined, fontWeight: promisedUnconfirmed ? 700 : 400 }}
              title={promisedUnconfirmed ? `UPS scheduled this for ${r.upsScheduledDate}, which is not the date we promised.` : undefined}>
              {r.promisedDate}{promisedUnconfirmed ? ' *' : ''}
            </span>
          ) : (
            <span style={{ color: '#c0392b', fontWeight: 700 }}>N/A</span>
          )}
        </td>

        {/* Actual. Blank if not yet delivered - her spec, and a blank here is
            meaningful, so it is never filled with a dash. */}
        <td style={{ ...TD, width: 92, textAlign: 'center' }}>
          {r.actualDate || r.upsDeliveredDate || ''}
          {!r.actualDate && !r.upsDeliveredDate && r.upsStatus && (
            <div style={{ fontSize: 9, color: 'var(--gray-400)' }}>{r.upsStatus}</div>
          )}
        </td>

        {/* Delay reason. Pre-filled from UPS, always editable. */}
        <td style={{ ...TD, width: 260 }}>
          <textarea
            rows={2}
            value={r.delayReason ?? ''}
            placeholder={r.upsReason || (r.upsChecked ? 'UPS gave no reason' : 'Checking UPS...')}
            onChange={(e) => patchRow(r.trackingNumber, { delayReason: e.target.value })}
            style={{ ...INPUT }}
            onFocus={(e) => { e.target.style.border = '1px solid var(--gray-300)'; e.target.style.background = '#fff'; }}
            onBlur={(e) => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent'; }}
          />
          {!r.delayReason && r.upsReason && (
            <button
              onClick={() => patchRow(r.trackingNumber, { delayReason: r.upsReason }, true)}
              style={{ fontSize: 9.5, border: 'none', background: 'none', color: 'var(--orange)', cursor: 'pointer', padding: '1px 0', fontWeight: 600 }}
            >Use what UPS said</button>
          )}
          {r.upsChecked && !r.upsReason && !r.upsError && (
            <div style={{ fontSize: 9, color: 'var(--gray-400)' }}>UPS posted no exception on this package.</div>
          )}
          {r.upsError && (
            <div style={{ fontSize: 9, color: '#c0392b' }} title={r.upsError}>Could not reach UPS for this one.</div>
          )}
          {exceptions.length > 0 && (
            <>
              <button
                onClick={() => setExpanded((x) => ({ ...x, [r.trackingNumber]: !isOpen }))}
                style={{ fontSize: 9.5, border: 'none', background: 'none', color: 'var(--gray-500)', cursor: 'pointer', padding: '1px 0' }}
              >{isOpen ? 'Hide' : `${exceptions.length} UPS note${exceptions.length === 1 ? '' : 's'}`}</button>
              {isOpen && (
                <div style={{ fontSize: 9.5, color: 'var(--gray-600, #555)', borderLeft: '2px solid var(--gray-200)', paddingLeft: 6, marginTop: 2 }}>
                  {exceptions.map((x, k) => (
                    <div key={k} style={{ marginBottom: 3 }}>
                      <b>{x.date ? `${x.date.slice(4, 6)}/${x.date.slice(6, 8)}` : ''}</b> {x.description}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </td>

        <td style={{ ...TD, width: 96 }}>
          <select value={r.assignedTo ?? ''} style={SELECT}
            onChange={(e) => patchRow(r.trackingNumber, { assignedTo: e.target.value }, true)}>
            <option value="">{DASH}</option>
            {(data?.assignees || []).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </td>

        <td style={{ ...TD, width: 178 }}>
          <select value={r.resolutionOffer ?? ''} style={SELECT}
            title={(data?.resolutionOffers || []).find((o) => o.value === r.resolutionOffer)?.tip || ''}
            onChange={(e) => patchRow(r.trackingNumber, { resolutionOffer: e.target.value })}>
            <option value="">{DASH}</option>
            {(data?.resolutionOffers || []).map((o) => (
              <option key={o.value} value={o.value} title={o.tip}>{o.value}</option>
            ))}
          </select>
          {r.resolutionOffer === 'Offer reship at 50%' && (
            <div style={{ fontSize: 9, color: 'var(--gray-500)', marginTop: 2 }}>
              For customer address errors, missing apt, wrong street, wrong zip, etc.
            </div>
          )}
        </td>

        <td style={{ ...TD, width: 210 }}>
          <textarea
            rows={2}
            value={r.notes ?? ''}
            onChange={(e) => patchRow(r.trackingNumber, { notes: e.target.value })}
            style={{ ...INPUT }}
            onFocus={(e) => { e.target.style.border = '1px solid var(--gray-300)'; e.target.style.background = '#fff'; }}
            onBlur={(e) => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent'; }}
          />
        </td>

        <td style={{ ...TD, width: 54, textAlign: 'center' }}>
          <input type="checkbox" checked={!!r.resolved} style={{ cursor: 'pointer' }}
            onChange={(e) => patchRow(r.trackingNumber, { resolved: e.target.checked }, true)} />
        </td>
      </tr>
    );
  };

  const HEAD = (
    <thead>
      <tr>
        <th style={{ ...TH, ...stickyCell(0, 3, 'var(--gray-50)'), width: COL_TRACK_W }}>Tracking</th>
        <th style={{ ...TH, ...stickyCell(COL_TRACK_W, 3, 'var(--gray-50)'), width: COL_ORDER_W }}>Order</th>
        <th style={{ ...TH, width: 62 }}>Ship</th>
        <th style={{ ...TH, width: 92, textAlign: 'center' }}>Expected</th>
        <th style={{ ...TH, width: 92, textAlign: 'center' }}>Actual</th>
        <th style={{ ...TH, width: 260 }}>Delay reason</th>
        <th style={{ ...TH, width: 96 }}>Assigned to</th>
        <th style={{ ...TH, width: 178 }}>Resolution offer</th>
        <th style={{ ...TH, width: 210 }}>Notes</th>
        <th style={{ ...TH, width: 54, textAlign: 'center' }}>Done</th>
      </tr>
    </thead>
  );

  return (
    <div>
      <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Delay Tracker</h2>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
            Late UPS packages, why they were late, and who is handling each one.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {saving > 0 && <span style={{ fontSize: 10.5, color: 'var(--gray-500)' }}>Saving...</span>}
          <button onClick={openSettings} className="btn btn-outline" style={{ fontSize: 11 }}>Email settings</button>
        </div>
      </div>

      {saveError && (
        <div className="card" style={{ marginBottom: 10, padding: '8px 14px', fontSize: 11.5, color: '#c0392b', background: '#fef3f0' }}>
          That last change did not save: {saveError}
        </div>
      )}

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Delivery date range</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>FROM</div>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="form-input" style={{ fontSize: 11 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>TO</div>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="form-input" style={{ fontSize: 11 }} />
          </div>
          <button onClick={() => load()} className="btn btn-primary" style={{ fontSize: 11 }}>Load</button>
          {[
            { label: 'This week', start: weekStartIso },
            { label: 'Last 7 days', start: () => daysAgoIso(7) },
            { label: 'Last 14 days', start: () => daysAgoIso(14) },
            { label: 'Last 30 days', start: () => daysAgoIso(30) },
          ].map((p) => (
            <button key={p.label} className="btn btn-ghost" style={{ fontSize: 11 }}
              onClick={() => { const s = p.start(); const e = todayIso(); setStart(s); setEnd(e); load({ start: s, end: e }); }}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {c && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'Late packages', value: c.total },
            { label: 'Open', value: c.open, highlight: c.open > 0 },
            { label: 'Not yet assigned', value: c.unassigned, highlight: c.unassigned > 0 },
            { label: 'Resolved', value: c.resolved },
          ].map((k) => (
            <div key={k.label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{k.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: k.highlight ? 'var(--orange)' : 'var(--navy)' }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* What UPS could and could not tell us. Printed rather than left to be
          inferred from blank cells, because "no reason" and "not looked up yet"
          look identical in a grid and mean completely different things. */}
      {c && (data?.outstandingUpsLookups > 0 || c.upsGaveNoReason > 0) && (
        <div className="card" style={{ marginBottom: 12, padding: '8px 14px', fontSize: 11.5, color: 'var(--gray-500)' }}>
          {data.outstandingUpsLookups > 0 && (
            <span>Reading UPS for {data.outstandingUpsLookups} more package{data.outstandingUpsLookups === 1 ? '' : 's'}, this refreshes on its own. </span>
          )}
          {c.upsGaveNoReason > 0 && (
            <span>UPS posted no reason on {c.upsGaveNoReason} of these, so those need a cause typed in by hand.</span>
          )}
        </div>
      )}

      {data?.pending?.count > 0 && (
        <div className="card" style={{ marginBottom: 12, padding: '8px 14px', fontSize: 11.5, background: '#fff8f2', color: 'var(--navy)' }}>
          <b>{data.pending.count}</b> assignment{data.pending.count === 1 ? '' : 's'} waiting to be emailed.
          {data.pending.due
            ? ' Going out on the next check.'
            : ` The batch sends about ${Math.max(0, data.windowMinutes - (data.pending.oldestAgeMin || 0))} minute(s) from now, so there is time to finish assigning.`}
          <button className="btn btn-ghost" style={{ fontSize: 10.5, marginLeft: 8 }}
            onClick={async () => {
              try { await delayTracker('notify', { force: true }); await load({ quiet: true }); }
              catch (e) { setSaveError(e.message); }
            }}>Send now</button>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          Late packages {rows.length > 0 && `(${open.length} open, ${done.length} resolved)`}
        </div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: '#c0392b' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>
            No late packages with a tracking number in that delivery date range.
          </div>
        )}
        {rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 11.5, minWidth: 1300 }}>
              {HEAD}
              <tbody>
                {open.map((r, i) => renderRow(r, i, false))}
                {done.length > 0 && (
                  <tr>
                    <td colSpan={10} style={{
                      padding: '8px 12px', background: 'var(--gray-100)',
                      borderTop: '2px solid var(--gray-300)', borderBottom: '1px solid var(--gray-200)',
                      fontSize: 10.5, fontWeight: 700, color: 'var(--gray-500)',
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                      position: 'sticky', left: 0,
                    }}>
                      Resolved ({done.length}) {DASH} kept here and in the history for this date range
                    </td>
                  </tr>
                )}
                {done.map((r, i) => renderRow(r, i, true))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showSettings && settings && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }} onClick={() => setShowSettings(false)}>
          <div className="card" style={{ maxWidth: 460, width: '100%', background: '#fff' }} onClick={(e) => e.stopPropagation()}>
            <div className="card-header">Assignment emails</div>
            <div style={{ padding: 16 }}>
              <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 12 }}>
                When assignments are saved, a timer starts from the first one. When it runs
                out, one batch goes out: the full list to the summary recipient, and a short
                "you have work waiting" to each person who was actually given something.
              </div>

              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 4 }}>
                  WAIT BEFORE SENDING ({settings.windowMin} to {settings.windowMax} minutes)
                </div>
                <input type="number" min={settings.windowMin} max={settings.windowMax}
                  value={settings.windowMinutes} className="form-input" style={{ fontSize: 12, width: 90 }}
                  onChange={(e) => setSettings((s) => ({ ...s, windowMinutes: e.target.value }))}
                  onBlur={async (e) => {
                    try { setSettings(await delayTracker('config', { windowMinutes: e.target.value })); }
                    catch (err) { setSaveError(err.message); }
                  }} />
              </div>

              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 6 }}>WHO GETS THEM</div>
              {(settings.recipients || []).map((rc) => (
                <label key={rc.email} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 12 }}>
                  <input type="checkbox" checked={rc.enabled}
                    onChange={async (e) => {
                      try { setSettings(await delayTracker('recipient', { email: rc.email, enabled: e.target.checked })); }
                      catch (err) { setSaveError(err.message); }
                    }} />
                  <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{rc.name}</span>
                  <span style={{ color: 'var(--gray-500)' }}>{rc.email}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 9.5, color: 'var(--gray-400)', textTransform: 'uppercase' }}>
                    {rc.kind === 'summary' ? 'full list' : 'nudge only'}
                  </span>
                </label>
              ))}

              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <button className="btn btn-navy" style={{ fontSize: 11 }} onClick={() => setShowSettings(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
